<template>
    <slot></slot>

    <div v-if="!state.locations_loaded" class="px-4 py-5 sm:p-6">
        <LoadingSection />
    </div>

    <fieldset v-if="state.locations_loaded">
        <div class="space-y-4 mb-8">
            <label v-for="(location, index) in state.locations" :key="index" class="group relative block cursor-pointer rounded-lg border bg-white pr-6 py-4 shadow-sm focus:outline-none sm:flex sm:justify-between hover:border-blue-500 transition" :class="state.current_location_id == location.id ? 'border-transparent border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'">
                <input v-model="state.current_location_id" type="radio" name="server-size" :value="location.id" class="sr-only" :aria-labelledby="`location-${location.id}-name`" :aria-describedby="`location-${location.id}-description`">
                <div class="flex-shrink-0 px-3 flex items-center justify-center">
                    <span class="transition rounded-full h-6 w-6 flex items-center justify-center group-hover:border-blue-500" :class="state.current_location_id == location.id ? 'bg-blue-500' : 'border border-2 border-gray-200'">
                        <CheckIcon v-show="state.current_location_id == location.id" class="text-white h-5 w-5" />
                    </span>
                </div>
                <span class="flex-1 items-center">
                    <span class="flex flex-col text-sm">
                        <span :id="`${instance_id}-location-${location.id}-name`" class="font-medium text-gray-900" v-html="location.name"></span>
                        <span :id="`${instance_id}-location-${location.id}-description`" class="text-gray-500">
                            <span class="block sm:inline" v-html="location.address_line_1"></span>
                            <span class="hidden sm:mx-1 sm:inline" aria-hidden="true">&middot;</span>
                            <span class="block sm:inline" v-html="location.town"></span>
                            <span class="hidden sm:mx-1 sm:inline" aria-hidden="true">&middot;</span>
                            <span class="block sm:inline" v-html="location.postcode"></span>
                        </span>
                    </span>
                </span>
                <span class="pointer-events-none absolute -inset-px rounded-lg border-1" :class="state.current_location_id == location.id ? 'border border-blue-500' : 'border-1 border-transparent'" aria-hidden="true"></span>
            </label>
            <label class="group relative block cursor-pointer rounded-lg border bg-white pr-6 py-4 shadow-sm focus:outline-none sm:flex sm:justify-between hover:border-blue-500 transition" :class="state.current_location_id == 'OTHER' ? 'border-transparent border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'">
                <input v-model="state.current_location_id" type="radio" name="server-size" :value="'OTHER'" class="sr-only">
                <div class="flex-shrink-0 px-3 flex items-center justify-center">
                    <span class="transition rounded-full h-6 w-6 flex items-center justify-center group-hover:border-blue-500" :class="state.current_location_id == 'OTHER' ? 'bg-blue-500' : 'border border-2 border-gray-200'">
                        <CheckIcon v-show="state.current_location_id == 'OTHER'" class="text-white h-5 w-5" />
                    </span>
                </div>
                <span class="flex-1 items-center">
                    <span class="flex flex-col text-sm">
                        <span class="font-medium text-gray-900">{{ no_location_label || 'Other' }}</span>
                    </span>
                </span>
            </label>
        </div>
        <div v-if="state.current_location_id == 'OTHER'">
            <FormField v-for="(field, key) in form.other_location.fields" :key="key" :field="field" :namespace="`${instance_id}_location`" :name="key" class="mb-4" />
        </div>
    </fieldset>
</template>

<script>
import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import { isValid } from '@lib/helpers'
import { useUserStore } from '@sto/user.store'
import * as LocationService from '@ser/location'

import CheckIcon from '@ico/CheckIcon.vue'
export default {
    props: ['current_location_id', 'current_location_other', 'auctioneer_id', 'no_location_label', 'instance_id'],
    components: {
        CheckIcon
    },
    setup(props, {emit}) {

        const USER_STORE = useUserStore()

        const supplied_current_location_id = props.current_location_id
            ? props.current_location_id
            : props.current_location_other
                ? 'OTHER'
                : null

        const state = reactive({
            locations_loading: false,
            locations_loaded: false,
            locations: [],
            current_location_id: supplied_current_location_id
        })

        const form = reactive({
            other_location: {
                fields: {
                    address_line_1: {
                        active: true,
                        label: 'Address line 1',
                        type: 'text',
                        value: props.current_location_other?.address_line_1 || null,
                        stored_value: props.current_location_other?.address_line_1 || null,
                        required: true,
                        pristine: false,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    address_line_2: {
                        active: true,
                        label: 'Address line 2',
                        type: 'text',
                        value: props.current_location_other?.address_line_2 || null,
                        stored_value: props.current_location_other?.address_line_2 || null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    address_line_3: {
                        active: true,
                        label: 'Address line 3',
                        type: 'text',
                        value: props.current_location_other?.address_line_3 || null,
                        stored_value: props.current_location_other?.address_line_3 || null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    town: {
                        active: true,
                        label: 'Town',
                        type: 'text',
                        value: props.current_location_other?.town || null,
                        stored_value: props.current_location_other?.town || null,
                        required: true,
                        pristine: false,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    county: {
                        active: true,
                        label: 'County',
                        type: 'text',
                        value: props.current_location_other?.county || null,
                        stored_value: props.current_location_other?.county || null,
                        required: true,
                        pristine: false,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    postcode: {
                        active: true,
                        label: 'Postcode',
                        type: 'text',
                        value: props.current_location_other?.postcode || null,
                        stored_value: props.current_location_other?.postcode || null,
                        required: true,
                        pristine: false,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    map_url: {
                        active: true,
                        label: 'Map URL',
                        type: 'text',
                        value: props.current_location_other?.map_url || null,
                        stored_value: props.current_location_other?.map_url || null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    what_three_words: {
                        active: true,
                        label: 'What Three Words',
                        type: 'text',
                        value: props.current_location_other?.what_three_words || null,
                        stored_value: props.current_location_other?.what_three_words || null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [
                            {type: 'characters', value: 'what_three_words'},
                        ],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    save_as_location: {
                        active: true,
                        label: 'Save to address book?',
                        type: 'toggle',
                        value: props.current_location_other?.save_as_location || false,
                        stored_value: props.current_location_other?.save_as_location || false,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    }
                }
            }
        })

        const setLocations = locations_data => {
            state.locations = locations_data

            if (state.current_location_id && state.current_location_id != 'OFFER') emit('location_change', state.locations.find(l => l.id == state.current_location_id))
        }

        const getLocations = async () => {

            if (state.locations_loading) return
            state.locations_loading = true

            let params = [{key: 'auctioneer_ids', value: props.auctioneer_id}]

            try {
                const locations = await LocationService.find(params)
                setLocations(locations)

            } catch (e) {
                console.error('LocationService.find error', e)
            } finally {
                state.locations_loading = false
                state.locations_loaded = true
            }

        }

        watch(
            () => state.current_location_id,
            (new_value, old_value) => {
                if (state.current_location_id == 'OTHER') {
                    let other_location = {
                        id: 'OTHER',
                        address_line_1: form.other_location.fields.address_line_1.value,
                        address_line_2: form.other_location.fields.address_line_2.value,
                        address_line_3: form.other_location.fields.address_line_3.value,
                        town: form.other_location.fields.town.value,
                        county: form.other_location.fields.county.value,
                        postcode: form.other_location.fields.postcode.value,
                        map_url: form.other_location.fields.map_url.value,
                        what_three_words: form.other_location.fields.what_three_words.value
                    }
                    return emit('location_change', other_location)

                }

                emit('location_change', state.locations.find(l => l.id == state.current_location_id))
            }
        )

        watch (
            () => [
                form.other_location.fields.address_line_1.value,
                form.other_location.fields.address_line_2.value,
                form.other_location.fields.address_line_3.value,
                form.other_location.fields.town.value,
                form.other_location.fields.county.value,
                form.other_location.fields.postcode.value,
                form.other_location.fields.map_url.value,
                form.other_location.fields.what_three_words.value,
                form.other_location.fields.save_as_location.value
            ],
            ([new_address_line_1, new_address_line_2, new_address_line_3, new_town, new_county, new_postcode, new_map_url, new_what_three_words]) => {

                let other_location = {
                    id: 'OTHER',
                    address_line_1: form.other_location.fields.address_line_1.value,
                    address_line_2: form.other_location.fields.address_line_2.value,
                    address_line_3: form.other_location.fields.address_line_3.value,
                    town: form.other_location.fields.town.value,
                    county: form.other_location.fields.county.value,
                    postcode: form.other_location.fields.postcode.value,
                    map_url: form.other_location.fields.map_url.value,
                    what_three_words: form.other_location.fields.what_three_words.value,
                    save_as_location: form.other_location.fields.save_as_location.value,
                    address_is_valid: isValid( form.other_location.fields)
                }
                return emit('location_change', other_location)
            }
        )

        watch(
            () => props.auctioneer_id,
            (new_value, old_value) => {
                getLocations();
            }
        )

        onBeforeMount(() => {
            getLocations()
        })

        onMounted(() => {
            if (state.current_location_id == 'OTHER') isValid(form.other_location.fields)
        })

        return {
            state,
            form
        }
    }
}
</script>